import { useState, useMemo } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { PasswordStrength, getStrengthScore } from '@/components/auth/PasswordStrength'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'

function formatApiError(err: any): string {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map((item: any) => item.msg || JSON.stringify(item)).join('. ')
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail)
  }
  return 'Failed to reset password. Please try again.'
}

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState({ password: false, confirm: false })

  const passwordError = useMemo(() => {
    if (!password) return 'Password is required'
    if (getStrengthScore(password) < 5) return 'Password does not meet all requirements'
    return ''
  }, [password])

  const confirmError = useMemo(() => {
    if (!confirm) return 'Please confirm your password'
    if (confirm !== password) return 'Passwords do not match'
    return ''
  }, [confirm, password])

  const canSubmit = !passwordError && !confirmError && !!token

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ password: true, confirm: true })
    setError('')

    if (!canSubmit) return

    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, new_password: password })
      setDone(true)
    } catch (err: any) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle2 className="h-7 w-7 text-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight">Password Reset Complete</h1>
          <p className="text-sm text-muted-foreground">
            Password updated.
          </p>
        </div>
        <Button asChild className="w-full bg-gradient-to-r from-violet-500 to-purple-600">
          <Link to="/login">Sign In</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1.5">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <KeyRound className="h-5 w-5 text-primary" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Set New Password</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <AuthAlert message={error} />}

        {!token && (
          <AuthAlert message="Invalid or missing reset token." />
        )}

        <div className="space-y-2">
          <Label htmlFor="password">New Password</Label>
          {/* CHECKPOINT: PRD-01 FR-1.1.3 — Reset password minimum 8 characters. */}
          <PasswordInput
            id="password"
            placeholder="8+ chars with upper, lower, digit, special"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            aria-invalid={touched.password && !!passwordError}
            aria-describedby="password-help"
            required
            autoFocus
          />
          {touched.password && passwordError ? (
            <p id="password-error" className="text-xs text-destructive">{passwordError}</p>
          ) : (
            <p id="password-help" className="text-xs text-muted-foreground">
              8+ characters with uppercase, lowercase, digit, and special character.
            </p>
          )}
          <PasswordStrength password={password} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm Password</Label>
          <PasswordInput
            id="confirm"
            placeholder="Repeat password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
            aria-invalid={touched.confirm && !!confirmError}
            aria-describedby={touched.confirm && confirmError ? 'confirm-error' : undefined}
            required
          />
          {touched.confirm && confirmError && (
            <p id="confirm-error" className="text-xs text-destructive">{confirmError}</p>
          )}
        </div>

        <AuthSubmitButton loading={loading} disabled={!canSubmit}>
          Reset Password
        </AuthSubmitButton>

        <div className="text-center">
          <Link to="/login" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center justify-center transition-colors">
            <ArrowLeft className="mr-1 h-3 w-3" />
            Back to Login
          </Link>
        </div>
      </form>
    </div>
  )
}
